/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable, NgZone } from '@angular/core';
import { PushNotificationsService } from '../push-notifications.service';
import { Subject } from 'rxjs';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';

import {
  PushNotifications,
  PushNotificationSchema,
  Token,
} from '@capacitor/push-notifications';
import {
  LocalNotifications,
  ScheduleOptions,
} from '@capacitor/local-notifications';
import { Device } from '@capacitor/device';
import { TranslateService } from '@ngx-translate/core';
import { LBSUtils } from 'src/app/_util/lbs-utils';
import { IMSAction } from 'src/app/_model/menu';

/**
 * A PushNotificationService implementation combining FCM with the Ionic implementation of PushNotifications
 */
@Injectable()
export class FcmPushNotificationsService implements PushNotificationsService {
  // Public Member
  public notification = new Subject<PushNotificationSchema>();
  public actionToPerform = new Subject<IMSAction>();
  public regId: Promise<{ id: string; type: string }>;

  // Private Member

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private zone: NgZone
  ) {
    Device.getInfo().then((info) => {
      if (info.platform === 'ios' || info.platform === 'android') {
        this.logger.log(this.constructor.name, 'Setup Push Notifications');

        this.setupPushNotifications();
      } else {
        this.regId = Promise.resolve({ id: '', type: '' });
      }
    });
    LocalNotifications.addListener('localNotificationActionPerformed', (notificationAction) => {
      const actionJSON = notificationAction?.notification?.actionTypeId
      if (actionJSON) {
        try {
          const action = JSON.parse(actionJSON) as IMSAction
          this.actionToPerform.next(action)
        } catch (e) {
          this.logger.error(this.constructor.name, "could not perform action of local notification", e?.message)
        }
      }
    })
  }

  // Public Functions
  public finishedBackgroundNotification(id: string) {}

  public async sendLocalNotification(notificationTitle: string, notificationBody: string, clickAction?: IMSAction) {
    const permStatus = await LocalNotifications.checkPermissions();
    if (permStatus.display != 'granted') {
      if (permStatus.display != 'denied') {
        LocalNotifications.requestPermissions();
      }
      return;
    }

    let scheduleOptions: ScheduleOptions = {
      notifications: [
        {
          title: notificationTitle,
          body: notificationBody,
          id: LBSUtils.randomIntFromInterval(-2147483648, 2147483647),
          smallIcon: 'notification_icon',
          largeIcon: 'notification_icon',
          autoCancel: true,
          actionTypeId: JSON.stringify(clickAction)
        },
      ],
    };
    LocalNotifications.schedule(scheduleOptions)
      .then((result) => {
        this.logger.log(
          this.constructor.name,
          ' Ionic Push Notification scheduled Local Noti:',
          result
        );
      })
      .catch((err) => {
        this.logger.log(
          this.constructor.name,
          ' Ionic Push Notification failed scheduling Local Noti:',
          err
        );
      });
  }

  // Private Functions
  private async setupPushNotifications() {
    this.regId = new Promise<{ id: string; type: string }>(
      async (resolve, reject) => {
        await PushNotifications.addListener('registration', (token: Token) => {
          console.info(
            this.constructor.name,
            'Registration token: ',
            token.value
          );

          this.logger.log(
            this.constructor.name,
            '- Registered Push Notifications with id:',
            token.value
          );
          resolve({ id: token.value, type: 'FCM' });
        });
        await PushNotifications.addListener('registrationError', (err) => {
          console.error(
            this.constructor.name,
            'Registration error: ',
            err.error
          );
          resolve({ id: undefined, type: 'FCM' });
        });
      }
    );

    await PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification) => {
        this.logger.log(
          this.constructor.name,
          'Ionic Received Push Notification',
          notification
        );

        if (notification.data?.message) {
          notification.body = notification.data?.message;
        }
        if (notification.data?.title) {
          notification.title = notification.data?.title;
        }
        if (notification.data?.count) {
          notification.badge = notification.data?.count;
        }
        try {
          if (notification.data?.additionalData) {
            if (notification.data?.additionalData['message']) {
              notification.data.additionalData['message'] = JSON.parse(
                notification.data?.additionalData['message']
              );
            }
            notification.notification = notification.data?.additionalData;
          }
        } catch (e) {
          // Fails on Android but needed for iOS
        }

        try {
          await this.zone.run(async () => {
            try {
              this.notification.next(notification);

              let pushBody = JSON.parse(notification.body);
              const isSlnt = pushBody['#imsapp']?.slnt ?? false;
              const code: string = pushBody['#imsapp']?.code;
              // These codes notify themselves: only after their sync is the complete
              // AC available, so their handlers can name the form, the owner, the time
              // and the device instead of a generic "a draft was released". Sending
              // here as well would produce two notifications for one push.
              //
              // `slnt` still applies to them, it is just evaluated one level down: the
              // action interpreter hands the flag to the handler, which then syncs
              // silently. See DraftPushNotifier.notify.
              const notifiesItself =
                code === 'new_draft_available' || code === 'draft_finalized';
              if (!isSlnt && !notifiesItself) {
                await this.sendLocalNotificationForCode(
                  code,
                  pushBody['#imsapp']?.notificationparams
                );
              }
            } catch (error) {
              this.logger.log(
                this.constructor.name,
                '- Error executing background push action',
                error?.message
              );
            }
          });
        } catch (error) {
          this.logger.log(
            this.constructor.name,
            '- Error executing background push action in zone',
            error?.message
          );
        }
      }
    );

    await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification) => {
        console.log(
          this.constructor.name,
          'Push notification action performed',
          notification.actionId,
          notification.inputValue
        );
      }
    );

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      this.logger.log(this.constructor.name, 'User denied permissions!');
    }

    await PushNotifications.register();
  }

  private async sendLocalNotificationForCode(code: string, params?: any) {
    if (code == undefined) {
      this.logger.warn(
        this.constructor.name,
        'Cannot send local noticiation for undefined code'
      );
      return;
    }

    this.logger.log(
      this.constructor.name,
      'Sending local notification for code',
      code,
      'with params',
      params
    );

    let notificationTitle: string;
    let notificationBody: string;

    switch (code) {
      case 'snc_extfiles':
        this.logger.log(
          this.constructor.name,
          'custom extfiles notification with documenttypes',
          params?.documenttypes,
          `(${params?.documenttypes ?? "EXTFILE"})`,
        );
        const documentTypes = (params?.documenttypes ?? "EXTFILE")
          .split(',') 
          .map(it => {
            return this.translate.instant(
              `SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.${code}.DOCUMENTTYPES.${it}`
            );
          })
          .join(', ')
          .replace(/, ([^,]*)$/, ` ${this.translate.instant(
            `SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.${code}.JOIN`
          )} $1`);

        notificationTitle = this.translate.instant(
          `SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.${code}.TITLE`, {
            documentTypes: documentTypes,
          }
        );
        notificationBody = this.translate.instant(
          `SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.${code}.BODY`, {
            documentTypes: documentTypes,
          }
        );
        break;
      default:
        notificationTitle = this.translate.instant(
          `SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.${code}.TITLE`
        );
        notificationBody = this.translate.instant(
          `SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.${code}.BODY`
        );
        break;
    }

    if (notificationBody === '' && notificationTitle === '') {
      this.logger.warn(
        this.constructor.name,
        'Could not find notification body nor title for code',
        code
      );
      return;
    }

    this.sendLocalNotification(notificationTitle, notificationBody)
  }

}
